import React, { useEffect, useState } from 'react';
import { axiosInstance } from "../../utils/axios.js";
import { MdDelete } from "react-icons/md";
import { FileImage, Upload, Plus, CheckCircle, AlertTriangle, File, Image, X } from 'lucide-react';
import '../ReserveCom/reserve.scss';
import AlertModal from '../common/AlertModal';

const MypageMedicalResultContainer = (props) => {
    const [medicalResults, setMedicalResults] = useState({
        insurance: [],
        health_check: [],
        other: []
    });
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [selectedFlag, setSelectedFlag] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUploadSuccess, setIsUploadSuccess] = useState(false);
    const [isDeleteSuccess, setIsDeleteSuccess] = useState(false);
    const [activeCategory, setActiveCategory] = useState('insurance');
    const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });

    // flagの定数定義
    const FLAGS = {
        INSURANCE_CARD_1: 1,      // 保険証または資格確認書①
        INSURANCE_CARD_2: 8,      // 保険証または資格確認書②
        BLOOD_TEST: 2,
        HEALTH_CHECKUP: 3,
        OTHER: 7
    };

    // 3つのメインカテゴリ定義
    const CATEGORIES = [
        { 
            key: 'insurance', 
            label: '保険証・資格確認書', 
            icon: '📄',
            description: '健康保険証または資格確認書（最大2枚）',
            flags: [FLAGS.INSURANCE_CARD_1, FLAGS.INSURANCE_CARD_2],
            placeholderTitle: '例：保険証①、資格確認書②'
        },
        { 
            key: 'blood_test', 
            label: '血液検査', 
            icon: '💉',
            description: '血液検査結果',
            flags: [FLAGS.BLOOD_TEST],
            placeholderTitle: '例：血液検査結果'
        },
        { 
            key: 'health_checkup', 
            label: '健康診断', 
            icon: '🩺',
            description: '健康診断結果',
            flags: [FLAGS.HEALTH_CHECKUP],
            placeholderTitle: '例：健康診断書'
        }
    ];

    // flag番号から日本語名を取得
    const getFlagName = (flag) => {
        switch(flag) {
            case FLAGS.INSURANCE_CARD_1: return '保険証または資格確認書①';
            case FLAGS.INSURANCE_CARD_2: return '保険証または資格確認書②';
            case FLAGS.BLOOD_TEST: return '血液検査';
            case FLAGS.HEALTH_CHECKUP: return '健康診断';
            default: return 'その他';
        }
    };

    useEffect(() => {
        fetchMedicalResults();
    }, []);

    const fetchMedicalResults = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/users/medical_result`, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'uid': props.token,
                    'Access-Control-Allow-Origin': '*'
                }
            });
            
            if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                // 新しいレスポンス形式に対応、3つのカテゴリに変換
                const convertedData = {
                    insurance: response.data.insurance || [],
                    blood_test: (response.data.health_check || []).filter(item => item.flag === FLAGS.BLOOD_TEST),
                    health_checkup: (response.data.health_check || []).filter(item => item.flag === FLAGS.HEALTH_CHECKUP)
                };
                setMedicalResults(convertedData);
            } else {
                // 旧形式の場合
                setMedicalResults({
                    insurance: [],
                    blood_test: [],
                    health_checkup: []
                });
            }
        } catch (error) {
            console.error('Error fetching medical results:', error);
            setMedicalResults({
                insurance: [],
                blood_test: [],
                health_checkup: []
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.post(`/users/medical_result_destroy/${id}`, {}, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'uid': props.token,
                    'Access-Control-Allow-Origin': '*'
                }
            });
            
            setFile(null);
            setTitle("");
            setIsModalOpen(false);
            setIsDeleteSuccess(true);
            fetchMedicalResults();
        } catch (error) {
            console.error('Error deleting medical result:', error);
            setAlertModal({ open: true, title: 'エラー', message: '削除に失敗しました。もう一度お試しください。' });
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file || !title) {
            setAlertModal({ open: true, title: 'エラー', message: "ファイル名とファイルの選択は必須です。" });
            return;
        }

        const formData = new FormData();
        formData.append("title", title);
        formData.append("file", file);
        formData.append("flag", selectedFlag);

        try {
            await axiosInstance.post(`/users/medical_result_create`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'uid': props.token,
                },
            });

            setFile(null);
            setTitle("");
            setSelectedFlag(FLAGS.INSURANCE_CARD_1);
            setIsModalOpen(false);
            setIsUploadSuccess(true);
            fetchMedicalResults();
        } catch (error) {
            console.error('Error uploading medical result:', error);
            setAlertModal({ open: true, title: 'エラー', message: 'アップロードに失敗しました。もう一度お試しください。' });
        }
    };

    const closeSuccessModal = () => {
        setIsUploadSuccess(false);
        setIsDeleteSuccess(false);
    };

    const openUploadModal = (category, specificFlag = null) => {
        setActiveCategory(category);
        
        if (specificFlag) {
            setSelectedFlag(specificFlag);
        } else {
            const categoryData = CATEGORIES.find(c => c.key === category);
            setSelectedFlag(categoryData?.flags[0] || FLAGS.OTHER);
        }
        
        setTitle("");
        setIsModalOpen(true);
    };

    const getCurrentCategoryData = () => {
        return CATEGORIES.find(c => c.key === activeCategory) || CATEGORIES[0];
    };

    // 通常のカテゴリ表示（血液検査・健康診断）
    const renderRegularCategory = (category) => {
        const categoryData = medicalResults[category.key] || [];
        
        return (
            <div className="medical_category_section">
                <div className="medical_category_header">
                    <div className="category_header_left">
                        <span className="category_icon">{category.icon}</span>
                        <div>
                            <h3 className="category_title">{category.label}</h3>
                        </div>
                    </div>
                    <button
                        onClick={() => openUploadModal(category.key)}
                        className="category_add_btn"
                        title={`${category.label}をアップロード`}
                    >
                        <Plus className="add_icon" />
                    </button>
                </div>

                {categoryData.length > 0 ? (
                    <div className="medical_files_list">
                        {categoryData.map((result) => (
                            <div key={result.id} className="medical_file_card">
                                <div className="medical_file_header">
                                    <div className="medical_file_info">
                                        {result.file_url?.endsWith(".pdf") ? (
                                            <File className="medical_file_type_icon pdf" />
                                        ) : (
                                            <Image className="medical_file_type_icon image" />
                                        )}
                                        <div>
                                            <h4 className="medical_file_title">{result.title}</h4>
                                        </div>
                                    </div>
                                    {result.deletable !== false && (
                                        <button 
                                            onClick={() => handleDelete(result.id)} 
                                            className="medical_file_delete_btn"
                                            title="削除"
                                        >
                                            <MdDelete className="delete_icon" />
                                        </button>
                                    )}
                                </div>

                                <div className="medical_file_preview">
                                    {result.file_url ? (
                                        result.file_url.endsWith(".pdf") ? (
                                            <div className="pdf_preview_container">
                                                <iframe 
                                                    src={result.file_url}
                                                    type="application/pdf"
                                                    className="pdf_preview"
                                                    title={result.title}
                                                />
                                                <div className="pdf_overlay">
                                                    <File className="pdf_icon" />
                                                    <p>PDFファイル</p>
                                                    <a href={result.file_url} target="_blank" rel="noopener noreferrer" className="pdf_open_btn">
                                                        開く
                                                    </a>
                                                </div>
                                            </div>
                                        ) : (
                                            <img
                                                src={result.file_url}
                                                alt={result.title}
                                                className="image_preview"
                                            />
                                        )
                                    ) : (
                                        <div className="no_file_preview">
                                            <FileImage className="no_file_icon" />
                                            <p>ファイルが添付されていません</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="medical_files_empty">
                        <FileImage className="medical_files_empty_icon" />
                        <p className="medical_files_empty_title">{category.label}がありません</p>
                        <p className="medical_files_empty_subtitle">ファイルをアップロードしましょう</p>
                    </div>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div>
                <div className="loader" style={{ width: '100px', height: '100px' }}></div>
                <div
                    style={{
                        width: '100%',
                        position: 'absolute',
                        bottom: '0%',
                        height: '80px',
                        background: '#FEFBF9',
                        zIndex: '12',
                    }}
                />
            </div>
        );
    }

    return (
        <div className="sunny_container" style={{paddingBottom:'120px'}}>
            <div className="medical_files_page">
                <div className="medical_files_header">
                    <FileImage className="medical_files_header_icon" />
                    <h2 className="medical_files_title">血液検査・健康診断結果</h2>
                </div>

                {/* 血液検査セクション */}
                {renderRegularCategory(CATEGORIES[1])}

                {/* 健康診断セクション */}
                {renderRegularCategory(CATEGORIES[2])}

                {/* アップロードモーダル */}
                {isModalOpen && (
                    <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
                        <div className="modal-content medical_upload_modal" onClick={(e) => e.stopPropagation()}>
                            <div className="medical_upload_header">
                                <div className="upload_header_info">
                                    <Upload className="upload_header_icon" />
                                    <h3>{getCurrentCategoryData().label}をアップロード</h3>
                                </div>
                                <button 
                                    onClick={() => setIsModalOpen(false)} 
                                    className="medical_upload_close"
                                >
                                    <X className="close_icon" />
                                </button>
                            </div>

                            <div className="medical_upload_form">
                                <div className="upload_form_field">
                                    <label className="upload_form_label">
                                        ファイル名
                                        <input
                                            type="text"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            required
                                            placeholder={getCurrentCategoryData().placeholderTitle}
                                            className="upload_form_input"
                                        />
                                    </label>
                                </div>

                                <div
                                    className={`upload_drop_zone ${file ? 'has_file' : ''}`}
                                    onClick={() => document.getElementById('medical-file-input').click()}
                                >
                                    <input
                                        id="medical-file-input"
                                        type="file"
                                        accept="image/*,application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => setFile(e.target.files[0])}
                                    />
                                    {file ? (
                                        <div className="file_preview_container">
                                            {file.type === 'application/pdf' ? (
                                                <div className="pdf_preview_uploaded">
                                                    <File className="uploaded_pdf_icon" />
                                                    <p className="uploaded_file_name">{file.name}</p>
                                                    <p className="uploaded_file_size">
                                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                                    </p>
                                                </div>
                                            ) : (
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt="preview"
                                                    className="uploaded_image_preview"
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="upload_placeholder">
                                            <Upload className="upload_placeholder_icon" />
                                            <p className="upload_placeholder_text">
                                                ここをクリックしてファイルを選択
                                            </p>
                                            <p className="upload_placeholder_subtext">
                                                画像ファイル・PDFファイル対応
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={handleUpload}
                                    disabled={!file || !title}
                                    className={`upload_submit_btn ${(!file || !title) ? 'disabled' : 'active'}`}
                                >
                                    <Upload className="upload_btn_icon" />
                                    アップロード
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 成功モーダル */}
                {isUploadSuccess && (
                    <div className="modal-overlay" onClick={closeSuccessModal}>
                        <div className="modal-content success_modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal_icon success_icon">
                                <CheckCircle className="icon" />
                            </div>
                            <p className="modal_title">アップロード完了</p>
                            <p className="modal_subtitle">ファイルが正常にアップロードされました。</p>
                            <button onClick={closeSuccessModal} className="success_modal_btn">
                                閉じる
                            </button>
                        </div>
                    </div>
                )}

                {isDeleteSuccess && (
                    <div className="modal-overlay" onClick={closeSuccessModal}>
                        <div className="modal-content success_modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal_icon success_icon">
                                <CheckCircle className="icon" />
                            </div>
                            <p className="modal_title">削除完了</p>
                            <p className="modal_subtitle">ファイルが正常に削除されました。</p>
                            <button onClick={closeSuccessModal} className="success_modal_btn">
                                閉じる
                            </button>
                        </div>
                    </div>
                )}
                
                <AlertModal 
                    open={alertModal.open}
                    title={alertModal.title}
                    message={alertModal.message}
                    onClose={() => setAlertModal({ open: false, title: '', message: '' })}
                />
            </div>
        </div>
    );
};

export default MypageMedicalResultContainer;